import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import DataTable from "../components/DataTable";
import PageHeader from "../components/PageHeader";
import { Badge, statusVariant } from "../components/Badge";
import { Trash2, Pencil, Ban, CheckCircle2, Wallet, FileText, X, KeyRound, Eye, ShieldCheck, Mail, Phone, Unlock, Lock, ShieldOff, Calendar, Download, Loader2, Send, Layers, Smartphone, Globe, Copy } from "lucide-react";
import { toast } from "sonner";
import { fmtBDT, fmtDateTime } from "../lib/format";
import NumberInput from "../../components/ui/NumberInput";

export default function Players() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState(null);
  const [pwdFor, setPwdFor] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [searchSeed, setSearchSeed] = useState("");
  const [catPermFor, setCatPermFor] = useState(null); // { row, categories: Set<string>, note: string }

  const load = async () => {
    setLoading(true);
    // Pick up a search query set by the top-bar SmartSearch (one-shot).
    let q = "";
    try {
      q = sessionStorage.getItem("admin-search-q") || "";
      if (q) {
        sessionStorage.removeItem("admin-search-q");
        setSearchSeed(q);
      }
    } catch { /* ignore */ }
    const params = q ? { search: q } : {};
    const { data } = await api.get("/players", { params });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (row) => {
    if (!window.confirm(`Delete player ${row.name}?`)) return;
    await api.delete(`/players/${row.id}`);
    toast.success("Player deleted");
    load();
  };

  const toggleBlock = async (row) => {
    const blocked = row.status === "blocked";
    await api.post(`/players/${row.id}/${blocked ? "unblock" : "block"}`);
    toast.success(blocked ? "Player unblocked" : "Player blocked");
    load();
  };

  const reset2FA = async (row) => {
    if (!window.confirm(`Disable Google Authenticator (2FA) for ${row.name}?\nThis will let them log in without a 6-digit code. Use only on emergency requests when the user lost their authenticator.`)) return;
    try {
      await api.post(`/site/admin/users/${row.id}/2fa/reset`);
      toast.success(`2FA disabled for ${row.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset 2FA");
    }
  };

  const toggleWithdrawPermission = async (row) => {    const currentlyAllowed = !!row.allow_non_agent_withdraw;
    const next = !currentlyAllowed;
    const msg = next
      ? `Allow ${row.name} to withdraw via non-agent channels (bKash/Nagad/Bank) even after agent deposit?`
      : `Revoke ${row.name}'s non-agent withdraw permission? They will be locked to agent-only withdrawals.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/players/${row.id}/withdraw-permission`, {
        allow_non_agent_withdraw: next,
        note: next ? "Granted by admin" : "Revoked by admin",
      });
      toast.success(next ? "Non-agent withdraw permission granted" : "Permission revoked — user is now agent-only");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const toggleAnyMethodPermission = async (row) => {
    const currentlyAllowed = !!row.allow_any_withdraw_method;
    const next = !currentlyAllowed;
    const lockedTo = (row.first_deposit_method || "").toUpperCase() || "first-deposit method";
    const msg = next
      ? `Allow ${row.name} to withdraw via ANY method (override the first-deposit-method lock — currently locked to ${lockedTo})?\n\nThis bypasses the anti-fraud restriction.`
      : `Re-lock ${row.name} to first-deposit-method-only withdrawals?\n\nThey will be restricted to ${lockedTo} again.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/players/${row.id}/any-method-permission`, {
        allow_any_withdraw_method: next,
        note: next ? "Granted by admin" : "Revoked by admin",
      });
      toast.success(next ? "Any-method withdraw unlocked" : "Re-locked to first-deposit method");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const openCategoryPermissions = (row) => {
    const initial = new Set(row.allowed_withdraw_categories || []);
    setCatPermFor({ row, categories: initial, note: "" });
  };

  const toggleCategory = (cat) => {
    setCatPermFor((prev) => {
      if (!prev) return prev;
      const next = new Set(prev.categories);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return { ...prev, categories: next };
    });
  };

  const saveCategoryPermissions = async () => {
    if (!catPermFor) return;
    try {
      const list = Array.from(catPermFor.categories);
      await api.post(`/players/${catPermFor.row.id}/category-permissions`, {
        categories: list,
        note: catPermFor.note || "Updated by admin",
      });
      toast.success(
        list.length === 0
          ? "Extra category permissions cleared"
          : `Allowed extras: ${list.map((c) => c.toUpperCase()).join(", ")}`
      );
      setCatPermFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      // Build a diff-based payload: only send fields that ACTUALLY changed vs
      // the row we opened for editing. Prevents no-op writes from tripping
      // Mongo unique-index errors (e.g. sending back the current phone as-is
      // would collide with another user's legacy identical phone).
      const orig = editing.__orig || {};
      const payload = {};
      if ((editing.name || "") !== (orig.name || "")) payload.full_name = editing.name;
      if ((editing.email || "") !== (orig.email || "")) payload.email = editing.email;
      if ((editing.mobile || "") !== (orig.mobile || "")) {
        // Row `mobile` is "<country_code> <phone>". Strip the country_code
        // prefix so we send only the local phone digits, not the country code.
        const raw = String(editing.mobile || "").trim();
        const cc = String(orig.country_code || "").trim();
        let phoneOnly = raw;
        if (cc && raw.startsWith(cc)) phoneOnly = raw.slice(cc.length).trim();
        // Fallback: split on space and take everything after the first token.
        else if (raw.includes(" ")) phoneOnly = raw.split(" ").slice(1).join(" ").trim();
        payload.phone = phoneOnly;
      }
      if (Number(editing.balance || 0) !== Number(orig.balance || 0)) payload.balance = Number(editing.balance);
      if ((editing.status || "") !== (orig.status || "")) payload.status = editing.status;
      if (!!editing.email_verified !== !!orig.email_verified) payload.email_verified = !!editing.email_verified;
      if (!!editing.phone_verified !== !!orig.phone_verified) payload.phone_verified = !!editing.phone_verified;
      if ((editing.kyc_status || "unverified") !== (orig.kyc_status || "unverified")) payload.kyc_status = editing.kyc_status || "unverified";

      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setEditing(null);
        return;
      }
      await api.patch(`/players/${editing.id}`, payload);
      toast.success("Player updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const saveAdjust = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/players/${adjusting.id}/adjust-balance`, {
        amount: Number(adjusting.amount),
        note: adjusting.note || "",
      });
      toast.success("Balance adjusted");
      setAdjusting(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Adjustment failed");
    }
  };

  const openHistory = async (row) => {
    setHistoryFor(row);
    setHistory(null);
    try {
      // Pull bets from the per-player history endpoint and the unified
      // site+agent successful-only deposit/withdraw trail from the referrals
      // endpoint. The referrals endpoint already filters out non-successful
      // rows and non-deposit/withdraw types — single source of truth.
      const [hist, ref] = await Promise.all([
        api.get(`/players/${row.id}/history`).catch(() => ({ data: { bets: [] } })),
        api.get(`/admin/referrals/user/${row.id}/transactions`).catch(() => ({ data: { items: [] } })),
      ]);
      const items = (ref.data?.items || []);
      const siteTx = items.filter((t) => (t.source || "site") === "site");
      const agentTx = items.filter((t) => t.source === "agent");
      setHistory({
        siteTransactions: siteTx,
        agentTransactions: agentTx,
        bets: hist.data?.bets || [],
        signup: hist.data?.signup || null,
      });
    } catch {
      setHistory({ siteTransactions: [], agentTransactions: [], bets: [], signup: null });
    }
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    if (!pwdFor) return;
    if (newPwd.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setPwdSaving(true);
    try {
      await api.post(`/players/${pwdFor.id}/change-password`, { new_password: newPwd });
      toast.success(`Password changed for ${pwdFor.name}`);
      setPwdFor(null);
      setNewPwd("");
      setShowPwd(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally {
      setPwdSaving(false);
    }
  };

  const resendWelcomeEmail = async (row) => {
    if (!row.email) {
      toast.error("Player has no email on file — add an email first");
      return;
    }
    if (!window.confirm(`Resend the Maxlive88 Welcome Email to ${row.name} (${row.email})?\n\nUse this only when the original welcome email failed to arrive.`)) return;
    try {
      const { data } = await api.post(`/players/${row.id}/resend-welcome-email`);
      toast.success(`Welcome email sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend welcome email");
    }
  };

  return (
    <div data-testid="players-page">
      <PageHeader title="Player Management" subtitle="Full control over registered players — edit, block, adjust balance, view history" testId="players-header" />

      <MonthlyTransactionsCard />

      <DataTable
        testId="players-table"
        loading={loading}
        rows={rows}
        initialSearch={searchSeed}
        columns={[
          { key: "name", label: "Name" },
          { key: "player_id", label: "Player ID", render: (r) => (
              <span className="font-mono text-xs tabular-nums tracking-wider text-emerald-700" data-testid={`players-pid-${r.id}`}>
                {r.player_id || <span className="text-gray-300">—</span>}
              </span>
            )
          },
          { key: "user_id", label: "Username" },
          { key: "email", label: "Email" },
          { key: "mobile", label: "Mobile" },
          { key: "balance", label: "Balance", align: "right", render: (r) => fmtBDT(r.balance) },
          { key: "first_deposit_category", label: "1st Deposit", render: (r) => {
              const cat = (r.first_deposit_category || "").toLowerCase();
              const method = (r.first_deposit_method || "").toUpperCase();
              const when = r.first_deposit_method_at ? new Date(r.first_deposit_method_at) : null;
              if (!cat) {
                return (
                  <span className="inline-flex items-center gap-1 text-[11px] text-gray-400" data-testid={`players-fcat-${r.id}`}>
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-gray-300"></span>
                    No deposit yet
                  </span>
                );
              }
              const styles = {
                ewallet: { bg: "bg-emerald-50",  br: "border-emerald-300", tx: "text-emerald-800", dot: "bg-emerald-500", label: "E-WALLET" },
                crypto:  { bg: "bg-amber-50",    br: "border-amber-300",   tx: "text-amber-800",   dot: "bg-amber-500",   label: "CRYPTO"   },
                bank:    { bg: "bg-sky-50",      br: "border-sky-300",     tx: "text-sky-800",     dot: "bg-sky-500",     label: "BANK"     },
                agent:   { bg: "bg-purple-50",   br: "border-purple-300",  tx: "text-purple-800",  dot: "bg-purple-500",  label: "AGENT"    },
              }[cat] || { bg: "bg-gray-50", br: "border-gray-300", tx: "text-gray-700", dot: "bg-gray-400", label: cat.toUpperCase() };
              return (
                <div className="flex flex-col gap-0.5" data-testid={`players-fcat-${r.id}`}>
                  <span
                    title={`First successful deposit category: ${styles.label}${method ? ` · method: ${method}` : ""}${when ? ` · ${when.toLocaleString()}` : ""}`}
                    className={`inline-flex items-center gap-1.5 px-1.5 py-0.5 border rounded-sm text-[10px] font-semibold uppercase tracking-wider w-fit ${styles.bg} ${styles.br} ${styles.tx}`}
                  >
                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${styles.dot}`}></span>
                    {styles.label}
                  </span>
                  {method && method !== styles.label && (
                    <span className="text-[10px] text-gray-500 font-mono">{method}</span>
                  )}
                  {when && (
                    <span className="text-[10px] text-gray-400">{when.toLocaleDateString()}</span>
                  )}
                </div>
              );
            }
          },
          { key: "verification", label: "Verification", render: (r) => (
              <div className="flex items-center gap-1.5" data-testid={`players-verif-${r.id}`}>
                <span title={r.email_verified ? "Email verified" : "Email NOT verified"} className={`inline-flex items-center justify-center h-6 w-6 rounded-sm border ${r.email_verified ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-gray-50 text-gray-300"}`} data-testid={`players-verif-email-${r.id}`}>
                  <Mail size={11} />
                </span>
                <span title={r.phone_verified ? "Phone verified" : "Phone NOT verified"} className={`inline-flex items-center justify-center h-6 w-6 rounded-sm border ${r.phone_verified ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-gray-50 text-gray-300"}`} data-testid={`players-verif-phone-${r.id}`}>
                  <Phone size={11} />
                </span>
                <span
                  title={`KYC: ${r.kyc_status || "unverified"}`}
                  className={`inline-flex items-center justify-center h-6 w-6 rounded-sm border ${
                    r.kyc_status === "verified" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                    : r.kyc_status === "pending" ? "border-amber-300 bg-amber-50 text-amber-700"
                    : r.kyc_status === "rejected" ? "border-red-300 bg-red-50 text-red-700"
                    : "border-gray-200 bg-gray-50 text-gray-300"
                  }`}
                  data-testid={`players-verif-kyc-${r.id}`}
                >
                  <ShieldCheck size={11} />
                </span>
              </div>
            )
          },
          { key: "status", label: "Status", render: (r) => <Badge variant={statusVariant(r.status)}>{r.status}</Badge> },
          { key: "created_at", label: "Joined", render: (r) => new Date(r.created_at).toLocaleDateString() },
        ]}
        actions={(row) => (
          <div className="flex flex-wrap items-center gap-1 justify-end">
            <button onClick={() => openHistory(row)} className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-gray-200 text-gray-700 rounded-sm hover:border-emerald-300 hover:text-emerald-700" data-testid={`players-history-${row.id}`}>
              <FileText size={12} /> History
            </button>
            <button onClick={() => setAdjusting({ id: row.id, name: row.name, amount: "", note: "" })} className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-gray-200 text-gray-700 rounded-sm hover:border-amber-300 hover:text-amber-700" data-testid={`players-adjust-${row.id}`}>
              <Wallet size={12} /> Adjust
            </button>
            <button onClick={() => setEditing({ ...row, __orig: row })} className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-gray-200 text-gray-700 rounded-sm hover:border-blue-300 hover:text-blue-700" data-testid={`players-edit-${row.id}`}>
              <Pencil size={12} /> Edit
            </button>
            <button onClick={() => { setPwdFor(row); setNewPwd(""); setShowPwd(false); }} className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-gray-200 text-gray-700 rounded-sm hover:border-purple-300 hover:text-purple-700" data-testid={`players-password-${row.id}`}>
              <KeyRound size={12} /> Password
            </button>
            <button onClick={() => resendWelcomeEmail(row)} title={row.email ? `Resend Welcome Email to ${row.email}` : "Player has no email on file"} disabled={!row.email} className={`h-8 px-2 inline-flex items-center gap-1 text-xs border rounded-sm ${row.email ? "border-gray-200 text-gray-700 hover:border-indigo-300 hover:text-indigo-700" : "border-gray-100 text-gray-300 cursor-not-allowed"}`} data-testid={`players-resend-welcome-${row.id}`}>
              <Send size={12} /> Resend Welcome
            </button>
            <button onClick={() => toggleWithdrawPermission(row)} title={row.allow_non_agent_withdraw ? "Revoke non-agent withdraw permission" : "Allow non-agent withdraw (override agent-deposit lock)"} className={`h-8 px-2 inline-flex items-center gap-1 text-xs border rounded-sm ${row.allow_non_agent_withdraw ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-gray-200 text-gray-700 hover:border-purple-300 hover:text-purple-700"}`} data-testid={`players-withdraw-perm-${row.id}`}>
              {row.allow_non_agent_withdraw ? <><Unlock size={12} /> Wd Open</> : <><Lock size={12} /> Wd Locked</>}
            </button>
            <button
              onClick={() => toggleAnyMethodPermission(row)}
              title={row.allow_any_withdraw_method
                ? "Revoke any-category permission — re-lock to first-deposit category"
                : `Allow withdraw via any category (currently locked to ${(row.first_deposit_category || row.first_deposit_method || "first-deposit category").toUpperCase()})`}
              className={`h-8 px-2 inline-flex items-center gap-1 text-xs border rounded-sm ${row.allow_any_withdraw_method ? "border-cyan-300 text-cyan-700 hover:bg-cyan-50" : "border-gray-200 text-gray-700 hover:border-cyan-300 hover:text-cyan-700"}`}
              data-testid={`players-anymethod-perm-${row.id}`}
            >
              {row.allow_any_withdraw_method
                ? <><Unlock size={12} /> Any Category</>
                : <><Lock size={12} /> {row.first_deposit_category
                    ? row.first_deposit_category.toUpperCase()
                    : (row.first_deposit_method ? row.first_deposit_method.slice(0, 6).toUpperCase() : "Cat")} Lock</>}
            </button>
            <button
              onClick={() => openCategoryPermissions(row)}
              title="Grant withdraw permission to specific extra categories (granular alternative to Any Category)"
              className={`h-8 px-2 inline-flex items-center gap-1 text-xs border rounded-sm ${(row.allowed_withdraw_categories || []).length > 0 ? "border-indigo-300 text-indigo-700 hover:bg-indigo-50" : "border-gray-200 text-gray-700 hover:border-indigo-300 hover:text-indigo-700"}`}
              data-testid={`players-cat-perms-${row.id}`}
            >
              <Layers size={12} />
              {(row.allowed_withdraw_categories || []).length > 0
                ? `+${(row.allowed_withdraw_categories || []).length} Extra`
                : "Categories"}
            </button>
            <button onClick={() => reset2FA(row)} title="Disable 2FA (Google Authenticator) for this player" className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-gray-200 text-gray-700 rounded-sm hover:border-rose-300 hover:text-rose-700" data-testid={`players-reset-2fa-${row.id}`}>
              <ShieldOff size={12} /> Reset 2FA
            </button>
            <button onClick={() => toggleBlock(row)} className={`h-8 px-2 inline-flex items-center gap-1 text-xs border rounded-sm ${row.status === "blocked" ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-red-200 text-red-700 hover:bg-red-50"}`} data-testid={`players-toggleblock-${row.id}`}>
              {row.status === "blocked" ? <><CheckCircle2 size={12} /> Unblock</> : <><Ban size={12} /> Block</>}
            </button>
            <button onClick={() => onDelete(row)} className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200" data-testid={`players-delete-${row.id}`}>
              <Trash2 size={14} />
            </button>
          </div>
        )}
      />

      {editing && (
        <Modal title="Edit Player" onClose={() => setEditing(null)}>
          <form onSubmit={saveEdit} className="space-y-3">
            <Field label="Full name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="edit-name" />
            <Field label="Email" type="email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} testId="edit-email" />
            <Field label="Mobile" value={editing.mobile} onChange={(v) => setEditing({ ...editing, mobile: v })} testId="edit-mobile" />
            <Field label="Balance (BDT)" type="number" value={editing.balance} onChange={(v) => setEditing({ ...editing, balance: v })} testId="edit-balance" />
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Status</label>
              <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="edit-status">
                <option value="active">Active</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>

            <div className="border-t border-gray-200 pt-3 mt-1">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Verification (admin override / skip)</div>
              <label className="flex items-center gap-3 p-2 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-2">
                <input
                  type="checkbox"
                  checked={!!editing.email_verified}
                  onChange={(e) => setEditing({ ...editing, email_verified: e.target.checked })}
                  data-testid="edit-email-verified"
                />
                <div className="flex items-center gap-1.5 text-sm text-gray-800"><Mail size={12} /> Email Verified</div>
              </label>
              <label className="flex items-center gap-3 p-2 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-2">
                <input
                  type="checkbox"
                  checked={!!editing.phone_verified}
                  onChange={(e) => setEditing({ ...editing, phone_verified: e.target.checked })}
                  data-testid="edit-phone-verified"
                />
                <div className="flex items-center gap-1.5 text-sm text-gray-800"><Phone size={12} /> Phone Verified</div>
              </label>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1 inline-flex items-center gap-1"><ShieldCheck size={12} /> KYC Status</label>
                <select
                  value={editing.kyc_status || "unverified"}
                  onChange={(e) => setEditing({ ...editing, kyc_status: e.target.value })}
                  className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                  data-testid="edit-kyc-status"
                >
                  <option value="unverified">Unverified</option>
                  <option value="pending">Pending</option>
                  <option value="verified">Verified (Skip / Approve)</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <p className="text-[11px] text-gray-500 mt-2">Tip: Set Email/Phone Verified or KYC = Verified to skip the user&apos;s verification step. To require it again, uncheck or set back to unverified.</p>
            </div>

            <button type="submit" className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-sm text-sm" data-testid="edit-submit">
              Save changes
            </button>
          </form>
        </Modal>
      )}

      {adjusting && (
        <Modal title={`Adjust Balance — ${adjusting.name}`} onClose={() => setAdjusting(null)}>
          <form onSubmit={saveAdjust} className="space-y-3">
            <Field label="Amount (positive to credit, negative to debit)" type="number" value={adjusting.amount} onChange={(v) => setAdjusting({ ...adjusting, amount: v })} testId="adjust-amount" />
            <Field label="Reason / Note" value={adjusting.note} onChange={(v) => setAdjusting({ ...adjusting, note: v })} testId="adjust-note" />
            <button type="submit" className="w-full h-10 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-sm text-sm" data-testid="adjust-submit">
              Apply adjustment
            </button>
          </form>
        </Modal>
      )}

      {catPermFor && (
        <Modal title={`Withdraw Category Permissions — ${catPermFor.row.name}`} onClose={() => setCatPermFor(null)}>
          <div className="space-y-4" data-testid="cat-perm-modal">
            <div className="text-xs text-gray-600 leading-relaxed bg-gray-50 border border-gray-200 rounded-sm p-3">
              <div className="mb-1.5">
                <span className="text-gray-500 font-semibold uppercase tracking-wider text-[10px]">First successful deposit</span>
              </div>
              {catPermFor.row.first_deposit_category ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 font-bold text-[11px] uppercase tracking-wider">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                      {catPermFor.row.first_deposit_category.toUpperCase()}
                    </span>
                    {catPermFor.row.first_deposit_method && (
                      <span className="font-mono text-[11px] text-gray-700">{catPermFor.row.first_deposit_method.toUpperCase()}</span>
                    )}
                    {catPermFor.row.first_deposit_method_at && (
                      <span className="text-[10px] text-gray-500">
                        {new Date(catPermFor.row.first_deposit_method_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5">
                    Withdrawals to this category are <b>always allowed</b>. Tick any <b>extra</b> categories below.
                  </div>
                </>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-700 text-[11px]">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500"></span>
                  No successful deposit yet — first-deposit category will be auto-detected on first credited deposit.
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "ewallet", label: "E-Wallet", note: "bKash · Nagad · Rocket · Upay" },
                { id: "crypto",  label: "Crypto",   note: "USDT · BTC · ETH · BNB · TON" },
                { id: "bank",    label: "Bank",     note: "Bank Transfer" },
                { id: "agent",   label: "Agent",    note: "Withdraw via Agent" },
              ].map((c) => {
                const isFirst = (catPermFor.row.first_deposit_category || "").toLowerCase() === c.id;
                const checked = catPermFor.categories.has(c.id);
                return (
                  <label
                    key={c.id}
                    className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer transition ${
                      isFirst
                        ? "bg-emerald-50 border-emerald-200 cursor-not-allowed opacity-80"
                        : checked
                          ? "border-indigo-300 bg-indigo-50"
                          : "border-gray-200 hover:border-indigo-300"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isFirst || checked}
                      disabled={isFirst}
                      onChange={() => toggleCategory(c.id)}
                      data-testid={`cat-perm-${c.id}`}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        {c.label}
                        {isFirst && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-200 text-emerald-800 rounded-sm">
                            First deposit
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{c.note}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                Note (audit log)
              </label>
              <input
                type="text"
                value={catPermFor.note}
                onChange={(e) => setCatPermFor({ ...catPermFor, note: e.target.value })}
                placeholder="e.g. VIP player exception — approved by manager"
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                data-testid="cat-perm-note"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={saveCategoryPermissions}
                className="flex-1 h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-sm text-sm"
                data-testid="cat-perm-save"
              >
                Save permissions
              </button>
              <button
                onClick={() => setCatPermFor(null)}
                className="h-10 px-4 border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-sm text-sm"
                data-testid="cat-perm-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {pwdFor && (
        <Modal title={`Change Password — ${pwdFor.name}`} onClose={() => { setPwdFor(null); setNewPwd(""); setShowPwd(false); }}>
          <form onSubmit={submitPasswordChange} className="space-y-4" data-testid="player-password-form">
            <div className="bg-purple-50 border border-purple-200 rounded-sm p-3 text-xs text-purple-800">
              Set a new password for <b>{pwdFor.name}</b> (<span className="font-mono">{pwdFor.user_id}</span>). The user will be able to log in immediately with this password. Existing sessions remain valid.
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">New password</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                  className="w-full h-10 pl-3 pr-10 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
                  data-testid="player-password-input"
                  placeholder="At least 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center text-gray-500 hover:text-gray-900 rounded-sm"
                  aria-label={showPwd ? "hide password" : "show password"}
                  data-testid="player-password-toggle-visibility"
                >
                  <Eye size={14} />
                </button>
              </div>
              <div className="text-[11px] text-gray-500 mt-1">Minimum 6 characters. Tell the user the new password through a secure channel.</div>
            </div>
            <button
              type="submit"
              disabled={pwdSaving || newPwd.length < 6}
              className="w-full h-10 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-sm text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
              data-testid="player-password-submit"
            >
              <KeyRound size={14} /> {pwdSaving ? "Saving..." : "Update password"}
            </button>
          </form>
        </Modal>
      )}

      {historyFor && (
        <Modal title={`History — ${historyFor.name}`} onClose={() => { setHistoryFor(null); setHistory(null); }} wide>
          {!history ? (
            <div className="py-10 text-center text-gray-500 text-sm">Loading history...</div>
          ) : (
            <div className="space-y-4" data-testid="player-history-modal">
              <div className="flex items-start justify-between gap-3 -mb-1">
                <div className="text-[10.5px] text-gray-500">
                  Showing only <span className="font-semibold text-gray-700">successful Deposits &amp; Withdrawals</span>.
                </div>
                <PdfExportButton playerId={historyFor.id} playerName={historyFor.name} />
              </div>
              <SignupDeviceCard signup={history.signup} />
              <HistorySection title="Site Transactions" rows={history.siteTransactions} renderRow={(r) => (
                <>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${r.type === "deposit" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{r.type}</span>
                  <span className="flex-1 text-gray-800">{fmtBDT(r.amount)}{r.method ? ` · ${r.method}` : ""}</span>
                  <Badge variant="success">APPROVED</Badge>
                  <span className="text-xs text-gray-500">{fmtDateTime(r.created_at)}</span>
                </>
              )} />
              <HistorySection title="Agent Transactions" rows={history.agentTransactions} renderRow={(r) => (
                <>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${r.type === "deposit" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{r.type}</span>
                  <span className="flex-1 text-gray-800">
                    {fmtBDT(r.amount)}
                    {r.agent_username ? <span className="text-gray-500"> · via @{r.agent_username}</span> : null}
                  </span>
                  <Badge variant="success">APPROVED</Badge>
                  <span className="text-xs text-gray-500">{fmtDateTime(r.created_at)}</span>
                </>
              )} />
              <HistorySection title="Bets" rows={history.bets} renderRow={(r) => {
                const stake = Number(r.stake || 0);
                const win = Number(r.win || 0);
                const net = Number(r.net_pnl ?? (win - stake));
                const status = (r.status || "").toLowerCase();
                const isWon = status === "won";
                const isLost = status === "lost";
                const isPush = status === "push";
                const isPending = status === "pending";
                const netColor = net > 0
                  ? "text-emerald-600"
                  : net < 0
                  ? "text-rose-600"
                  : "text-gray-500";
                return (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate">{r.game}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 tabular-nums">
                        <span>Stake: <b className="text-gray-700">{fmtBDT(stake)}</b></span>
                        <span>Win: <b className={win > 0 ? "text-emerald-600" : "text-gray-700"}>{fmtBDT(win)}</b></span>
                        <span>Net: <b className={netColor}>{net >= 0 ? "+" : ""}{fmtBDT(net)}</b></span>
                        {r.round_id && <span className="text-gray-400">#{String(r.round_id).slice(-8)}</span>}
                      </div>
                    </div>
                    <Badge variant={isWon ? "success" : isLost ? "danger" : isPush ? "pending" : isPending ? "info" : "default"}>
                      {isWon ? "WON" : isLost ? "LOST" : isPush ? "PUSH" : isPending ? "PENDING" : (r.status || "—").toUpperCase()}
                    </Badge>
                    <span className="text-xs text-gray-500 shrink-0">{fmtDateTime(r.created_at)}</span>
                  </>
                );
              }} />
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  const isNumber = type === "number";
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</label>
      {isNumber ? (
        <NumberInput
          value={value}
          onChange={(raw) => onChange(raw)}
          asString
          className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
          data-testid={testId}
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
          data-testid={testId}
        />
      )}
    </div>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-sm w-full ${wide ? "max-w-3xl" : "max-w-md"} max-h-[90vh] overflow-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="font-display font-bold text-base">{title}</h3>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center text-gray-500 hover:text-gray-900 rounded-sm">
            <X size={16} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function PdfExportButton({ playerId, playerName }) {
  const [busy, setBusy] = useState(false);
  const onExport = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.get(`/players/${playerId}/transactions-pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (playerName || "player").replace(/\s+/g, "_");
      a.download = `maxlive88_transactions_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate PDF");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={onExport}
      disabled={busy}
      data-testid="player-history-export-pdf"
      className="inline-flex items-center gap-1.5 h-7 px-2.5 text-[11px] font-semibold border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300 rounded-sm disabled:opacity-50 transition-colors"
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
      {busy ? "Generating..." : "Export PDF"}
    </button>
  );
}

function HistorySection({ title, rows, renderRow }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">{title} ({rows.length})</div>
      <div className="border border-gray-200 rounded-sm max-h-56 overflow-auto">
        {rows.length === 0 ? (
          <div className="text-xs text-gray-500 p-4">None</div>
        ) : rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm border-b border-gray-100 last:border-0">
            {renderRow(r)}
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupDeviceCard({ signup }) {
  const copyValue = async (value, label) => {
    if (!value) return;
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`, { description: value }); }
    catch {
      const ta = document.createElement("textarea"); ta.value = value; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success(`${label} copied`); } catch { toast.error("Copy failed"); }
      document.body.removeChild(ta);
    }
  };
  const s = signup || {};
  const hasAny = s.device_id || s.ip;
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50/60 p-3" data-testid="player-signup-device">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
        <ShieldCheck size={13} className="text-emerald-600" /> Signup Device &amp; IP
        {s.registered_at ? <span className="ml-auto font-normal normal-case text-gray-400">{fmtDateTime(s.registered_at)}</span> : null}
      </div>
      {!hasAny ? (
        <div className="text-xs text-gray-400">No device / IP recorded for this account.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white border border-gray-200 rounded-md p-2.5">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1"><Smartphone size={12} /> Device ID</div>
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-xs text-gray-800 break-all" title={s.device_id}>{s.device_id || "—"}</span>
              {s.device_id ? <button onClick={() => copyValue(s.device_id, "Device ID")} className="h-6 w-6 shrink-0 inline-flex items-center justify-center text-gray-400 hover:text-blue-600 rounded" title="Copy Device ID" data-testid="copy-signup-device"><Copy size={12} /></button> : null}
            </div>
            <div className="flex items-center gap-1 mt-1.5 flex-wrap">
              {s.device_account_count > 1 && <Badge variant="danger">{s.device_account_count} accounts</Badge>}
              {s.device_whitelisted && <Badge variant="success">allowed</Badge>}
              {s.device_blocked && <Badge variant="danger">banned</Badge>}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-md p-2.5">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1"><Globe size={12} /> IP Address</div>
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-xs text-gray-800" title={s.ip}>{s.ip || "—"}</span>
              {s.ip ? <button onClick={() => copyValue(s.ip, "IP")} className="h-6 w-6 shrink-0 inline-flex items-center justify-center text-gray-400 hover:text-blue-600 rounded" title="Copy IP" data-testid="copy-signup-ip"><Copy size={12} /></button> : null}
            </div>
            <div className="flex items-center gap-1 mt-1.5 flex-wrap">
              {s.ip_account_count > 1 && <Badge variant="pending">{s.ip_account_count} accounts</Badge>}
              {s.ip_whitelisted && <Badge variant="success">allowed</Badge>}
              {s.ip_blocked && <Badge variant="danger">banned</Badge>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function MonthlyTransactionsCard() {
  const currentMonth = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);
  const [month, setMonth] = useState(currentMonth);
  const [busy, setBusy] = useState(false);

  const monthOptions = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString("en-GB", { month: "long", year: "numeric" });
      out.push({ value, label });
    }
    return out;
  }, []);

  const monthLabel = useMemo(() => {
    const found = monthOptions.find((o) => o.value === month);
    return found?.label || month;
  }, [monthOptions, month]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get("/reports/players/monthly-transactions", {
        params: { month },
        responseType: "blob",
      });
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const fname = (m && m[1]) || `players_transactions_${month}.pdf`;
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fname;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      toast.success(`Downloaded ${fname}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to generate PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-emerald-50 via-white to-cyan-50 border border-emerald-200 rounded-sm p-4 mb-5 flex flex-col md:flex-row md:items-end gap-3" data-testid="players-monthly-txn-card">
      <div className="flex items-start gap-3 md:flex-1">
        <div className="h-10 w-10 rounded-sm bg-emerald-500 text-white inline-flex items-center justify-center shrink-0">
          <FileText size={18} />
        </div>
        <div>
          <div className="font-display text-base font-bold text-gray-900">Monthly Transaction History (PDF)</div>
          <div className="text-[12px] text-gray-600 leading-relaxed mt-0.5 max-w-xl">
            Download every player&apos;s deposits, withdrawals & bonus/adjustment line items for the chosen month — grouped by player with per-player totals and a global summary. Great for compliance, AML, and monthly book-keeping.
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1.5">
            <Calendar size={11} /> Reporting Month
          </label>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="players-monthly-txn-month"
            className="h-9 px-3 text-sm border border-gray-300 rounded-sm focus:outline-none focus:border-emerald-500 bg-white min-w-[180px]"
          >
            {monthOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={busy}
          data-testid="players-monthly-txn-download"
          title={`Download all-player transaction history for ${monthLabel}`}
          className="h-9 px-3.5 inline-flex items-center gap-2 text-xs font-semibold rounded-sm border border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600 hover:border-emerald-600 transition-colors disabled:opacity-60 disabled:cursor-wait"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {busy ? "Generating PDF…" : "Download PDF"}
        </button>
      </div>
    </div>
  );
}
